import { Injectable } from '@angular/core';
import { ICPAdTestConfig } from '@portal/app/geo-designer/models/ICPAdTestConfig';
import {
  ApiService,
  CpCellType,
  ICampaign,
  IConversionType,
  ICreateGeoTestRequest,
  IDSConversionTypeOutput,
  IGeoCell,
  IGeoTest,
  IListCampaignsRequest,
  IRecommendedTest,
  IScaleCostCampaignRequest,
  IScaleCostCampaignResponse,
  IUpdateGeoTestRequest,
  Status
} from '@libs/apis';
import { BrandStoreService } from '@portal/app/dashboard/geo-doe-config/data-stores/brand-store.service';
import { BehaviorSubject, Observable, of, take } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { GeoTestDetails } from '@portal/app/geo-designer/models/GeoTestDetails';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { setSavedTestDatesWithoutTimezone } from '@portal/app/dashboard/geo-doe-config/shared/utils';
import { ITestScheduleInfo } from '@portal/app/geo-designer/models/ITestScheduleInfo';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import { GeoDesignerConstants } from '@portal/app/geo-designer/common/geo-designer.constants';
import { IGeoTestEditableParams } from '@portal/app/geo-designer/models/IGeoTestEditableParams';
import { isEqual } from 'lodash-es';
import { GeoTestPersistenceStatus } from '@portal/app/geo-designer/models/GeoTestPersistenceStatus';
import dayjs from 'dayjs';
import { IPlatform } from '@portal/app/dashboard/geo-doe-config/models';
import { TestStatusHelper } from '@portal/app/geo-designer/common/test-status.helper';

@Injectable({
  providedIn: 'root'
})
export class GeoTestsService {
  private geoTests$: BehaviorSubject<IGeoTest[]> = new BehaviorSubject<
    IGeoTest[]
  >([]);

  public allGeoTests$ = this.geoTests$.asObservable();

  private conversionTypes$: BehaviorSubject<IConversionType[]> =
    new BehaviorSubject<IConversionType[]>([]);

  private selectedConversionTypeId$ = new BehaviorSubject<number | null>(null);

  public readonly selectedConversionTypeId =
    this.selectedConversionTypeId$.asObservable();

  private recommendedTests$: BehaviorSubject<IRecommendedTest[]> =
    new BehaviorSubject<IRecommendedTest[]>([]);

  private geoTestDetails$: BehaviorSubject<GeoTestDetails> =
    new BehaviorSubject<GeoTestDetails>(new GeoTestDetails(null, null));

  private dsMdlSimulationReferenceData$: BehaviorSubject<
    IDSConversionTypeOutput[]
  > = new BehaviorSubject<IDSConversionTypeOutput[]>([]);

  private testScheduleInfo$: BehaviorSubject<ITestScheduleInfo> =
    new BehaviorSubject<ITestScheduleInfo>({
      startDate: null,
      endDate: null
    });

  private campaigns$: BehaviorSubject<{
    requestKey: Partial<IListCampaignsRequest>;
    campaigns: ICampaign[];
  }> = new BehaviorSubject<{
    requestKey: Partial<IListCampaignsRequest>;
    campaigns: ICampaign[];
  }>({
    requestKey: {},
    campaigns: []
  });

  private testName$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private businessQuestion$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  private geoTestEditableParams$: BehaviorSubject<IGeoTestEditableParams | null> =
    new BehaviorSubject<IGeoTestEditableParams | null>(null);

  private geoCell$: BehaviorSubject<IGeoCell | null> =
    new BehaviorSubject<IGeoCell | null>(null);

  private isCalendarReady$ = new BehaviorSubject<boolean>(false);

  geoTestResultConversionTimeLineChartAPICompleted =
    new BehaviorSubject<boolean>(false);

  private geoTestCreatedOrUpdated$: BehaviorSubject<{
    studyId: number;
    status: GeoTestPersistenceStatus;
  }> = new BehaviorSubject<{
    studyId: number;
    status: GeoTestPersistenceStatus;
  }>({
    studyId: 0,
    status: GeoTestPersistenceStatus.noOp
  });

  geoTestCreatedOrUpdated = this.geoTestCreatedOrUpdated$.asObservable();
  platformInfo: IPlatform[] = [];

  constructor(
    private readonly apiService: ApiService,
    private readonly brandStoreService: BrandStoreService,
    private readonly selectionService: SelectionService
  ) {
    this.selectionService.selectionChanged.subscribe(() => {
      this.initializeService();
    });
  }

  initializeService(): void {
    this.geoTests$ = new BehaviorSubject<IGeoTest[]>([]);

    this.conversionTypes$ = new BehaviorSubject<IConversionType[]>([]);

    this.recommendedTests$ = new BehaviorSubject<IRecommendedTest[]>([]);

    this.dsMdlSimulationReferenceData$ = new BehaviorSubject<
      IDSConversionTypeOutput[]
    >([]);

    this.geoTestDetails$ = new BehaviorSubject<GeoTestDetails>(
      new GeoTestDetails(null, null)
    );

    this.dsMdlSimulationReferenceData$ = new BehaviorSubject<
      IDSConversionTypeOutput[]
    >([]);

    this.testScheduleInfo$ = new BehaviorSubject<ITestScheduleInfo>({
      startDate: null,
      endDate: null
    });

    this.geoCell$ = new BehaviorSubject<IGeoCell | null>(null);

    this.geoTestEditableParams$ =
      new BehaviorSubject<IGeoTestEditableParams | null>(null);
  }

  getAllGeoTests(forceRefresh?: boolean): Observable<IGeoTest[]> {
    if (!this.geoTests$.value.length || forceRefresh) {
      return fromPromise(
        this.apiService.geoDoeApisOperations.getAllGeoTests(
          this.brandStoreService.brand.clientId,
          this.brandStoreService.brand.brandId,
          [Status.ACTIVE, Status.DRAFT]
        )
      ).pipe(
        map((tests) => {
          const geoTests = tests as IGeoTest[];
          return geoTests.map((geoTest: IGeoTest) => {
            this.mapStatus(geoTest.geoStudy as ICPAdTestConfig);
            (geoTest.geoStudy as ICPAdTestConfig).bauCell =
              geoTest.geoStudy.cellConfigs.find(
                (cellConfig) => cellConfig.cellType === CpCellType.BAU
              ) || null;
            setSavedTestDatesWithoutTimezone(geoTest.geoStudy);
            return geoTest;
          });
        }),
        tap((tests) => this.geoTests$.next(tests))
      );
    }

    return this.geoTests$.asObservable().pipe(take(1));
  }

  isDataLoaded(): boolean {
    return !!(
      this.geoCell$.value &&
      this.geoTests$.value &&
      this.isCalendarReady$.value
    );
  }

  getCalendarState(): Observable<boolean> {
    return this.isCalendarReady$.asObservable();
  }

  setCalendarReady(status = true): void {
    this.isCalendarReady$.next(status);
  }

  mapStatus(geoTest: ICPAdTestConfig): void {
    const { actualStatus, displayStatus } =
      TestStatusHelper.getDisplayStatus(geoTest);

    geoTest.actualStatus = actualStatus;
    geoTest.displayStatus = displayStatus;
  }

  getAllConversionTypes(): Observable<IConversionType[]> {
    if (!this.conversionTypes$.value.length) {
      return fromPromise(
        this.apiService.manageFactDataApisOperations
          .getAllConversionTypes(
            this.brandStoreService.brand.clientId,
            Status.ACTIVE,
            this.brandStoreService.brand.brandId
          )
          .then((conversionTypes) => {
            this.conversionTypes$.next(conversionTypes);
            return conversionTypes;
          })
      ).pipe(take(1));
    }

    return this.conversionTypes$.asObservable().pipe(take(1));
  }

  getConversionTypeById(
    conversionTypeId: number
  ): Observable<IConversionType | null> {
    return this.getAllConversionTypes().pipe(
      map((conversionTypes) => {
        const index = conversionTypes.findIndex(
          (conversionType) => conversionType.id === conversionTypeId
        );

        return conversionTypes[index] || null;
      })
    );
  }

  public getAllRecommendedTests(): Observable<IRecommendedTest[]> {
    if (!this.recommendedTests$.value.length) {
      return fromPromise(
        this.apiService.geoDoeApisOperations.getAllTestRecommendations(
          this.brandStoreService.brand.clientId,
          this.brandStoreService.brand.brandId
        )
      ).pipe(
        tap((recommendedTests) => this.recommendedTests$.next(recommendedTests))
      );
    }

    return this.recommendedTests$.asObservable().pipe(take(1));
  }

  public getGeoTestDetails(): Observable<GeoTestDetails> {
    return this.geoTestDetails$.asObservable();
  }

  public setGeoTestDetails(geoTestDetails: GeoTestDetails): void {
    this.geoTestDetails$.next(geoTestDetails);
  }

  public clearGeoTestDetails(): void {
    this.geoTestDetails$.next(new GeoTestDetails(null, null));
  }

  setRecommendedTest(recommendedTest: IRecommendedTest) {
    this.geoTestDetails$.next(
      new GeoTestDetails(
        recommendedTest,
        this.geoTestDetails$.value.geoTestObject
      )
    );
  }

  setGeoStudy(geoTest: IGeoTest) {
    this.geoTestDetails$.next(
      new GeoTestDetails(geoTest.recommendedTest, geoTest)
    );
    // this.testName$.next(geoTest.geoStudy.cellConfigs[0].cellName);
  }

  private getRequiredRecommendedTestParams() {
    const { clientId, brandId } = this.brandStoreService.brand;
    const recommendedTestId = this.geoTestDetails$.value.recommendedTest?.id;
    const conversionTypeId =
      this.geoTestDetails$.value.recommendedTest?.conversionTypeId || 0;
    const startDate = DateTimeService.dateString(
      this.testScheduleInfo$.value.startDate || new Date(),
      GeoDesignerConstants.apiDateFormat
    );
    const endDate = DateTimeService.dateString(
      this.testScheduleInfo$.value.endDate || new Date(),
      GeoDesignerConstants.apiDateFormat
    );

    return {
      clientId,
      brandId,
      recommendedTestId,
      conversionTypeId,
      startDate,
      endDate
    };
  }

  getRecommendedGeoCell(silentCall = false): Observable<IGeoCell> {
    const {
      clientId,
      brandId,
      recommendedTestId,
      conversionTypeId,
      startDate,
      endDate
    } = this.getRequiredRecommendedTestParams();

    if (this.geoCell$.value && !silentCall) {
      if (
        this.geoCell$.value?.recommendedTestId === recommendedTestId &&
        this.geoCell$.value?.startDate === startDate &&
        this.geoCell$.value?.endDate === endDate
      ) {
        return of(this.geoCell$.value);
      }
    }
    return fromPromise(
      this.apiService.geoDoeApisOperations.getGeoRegionsForRecommendation(
        clientId,
        brandId,
        conversionTypeId,
        startDate,
        endDate
      )
    ).pipe(
      tap((geoCell) => {
        if (!silentCall) {
          this.setGeoCell(geoCell, {
            recommendedTestId,
            startDate,
            endDate
          });
        }
        return geoCell;
      })
    );
  }

  getExistingTestGeoCell(): Observable<IGeoCell> {
    const { geoStudy, isV2, cellConfigId } = this.geoTestDetails$.value;
    if (
      this.geoCell$.value &&
      this.geoCell$.value?.cellConfigId === cellConfigId
    ) {
      return of(this.geoCell$.value);
    }
    return fromPromise(
      this.apiService.geoDoeApisOperations.getExistingGeoTestGeoCell(
        geoStudy?.studyId || 0,
        isV2,
        geoStudy?.cellConfigs[0]?.msCell || '',
        geoStudy?.cellConfigs[0]?.geoLocationIds || []
      )
    ).pipe(
      tap((geoCell) => {
        this.setGeoCell(geoCell, {
          cellConfigId
        });

        return geoCell;
      })
    );
  }

  getDSInfoForConversionType(
    conversionTypeId: number
  ): Observable<IDSConversionTypeOutput | undefined> {
    if (!this.dsMdlSimulationReferenceData$.value.length) {
      return fromPromise(
        this.apiService.geoDoeApisOperations
          .getDSMdlSimulationReferenceData(
            this.brandStoreService.brand.clientId,
            this.brandStoreService.brand.brandId
          )
          .then((referenceDSOutput) => {
            const enrichedReferenceData = referenceDSOutput.map(
              (dsReferenceData) => {
                const lastUpdatedDate = dayjs(dsReferenceData?.executionId);
                //TODO: Sept 30th as fixed next update date to be removed once all MS have been updated manually
                return {
                  ...dsReferenceData,
                  lastUpdated: lastUpdatedDate.toDate(),
                  nextUpdate: lastUpdatedDate.isBefore(dayjs('2024-07-01'))
                    ? dayjs('2024-09-30').toDate()
                    : dayjs(dsReferenceData?.executionId)
                        .add(
                          GeoDesignerConstants.nextMarketSelectionUpdateInDays,
                          'days'
                        )
                        .toDate()
                };
              }
            );
            this.dsMdlSimulationReferenceData$.next(enrichedReferenceData);
            return enrichedReferenceData.filter(
              this.dsMdlReferenceDataFilterPredicate(conversionTypeId)
            );
          })
      ).pipe(
        map((referenceData) => {
          return referenceData[0];
        }),
        take(1)
      );
    }

    return this.dsMdlSimulationReferenceData$
      .asObservable()
      .pipe(
        map((data) =>
          data.find(this.dsMdlReferenceDataFilterPredicate(conversionTypeId))
        )
      );
  }

  private dsMdlReferenceDataFilterPredicate(conversionTypeId: number) {
    return (referenceDsOutput: IDSConversionTypeOutput) =>
      referenceDsOutput.conversionTypeId === conversionTypeId;
  }

  getTestScheduleInfo(): Observable<ITestScheduleInfo> {
    return this.testScheduleInfo$
      .asObservable()
      .pipe(
        filter(
          (testSchedule) => !!testSchedule.startDate && !!testSchedule.endDate
        )
      );
  }

  setTestScheduleInfo(startDate: Date, endDate: Date): void {
    const previousStartDate = this.testScheduleInfo$.value.startDate;
    const previousEndDate = this.testScheduleInfo$.value.endDate;

    if (
      startDate.getTime() !== previousStartDate?.getTime() ||
      endDate.getTime() !== previousEndDate?.getTime()
    ) {
      this.testScheduleInfo$.next({
        startDate,
        endDate
      });
    }
  }

  getTestCampaigns(
    listCampaignsRequest: Partial<IListCampaignsRequest>,
    silentCall = false,
    isPDF = false
  ): Observable<ICampaign[]> {
    listCampaignsRequest.clientId = this.brandStoreService.brand.clientId;
    listCampaignsRequest.brandId = this.brandStoreService.brand.brandId;

    if (
      isEqual(this.campaigns$.value.requestKey, listCampaignsRequest) ||
      isPDF
    ) {
      return of(this.campaigns);
    }

    if (!silentCall) {
      this.campaigns$.next({
        requestKey: {},
        campaigns: []
      });
    }

    return fromPromise(
      this.apiService.geoDoeApisOperations.getGeoTestCampaigns(
        listCampaignsRequest
      )
    ).pipe(
      tap((campaigns) => {
        if (!silentCall) {
          this.campaigns$.next({
            requestKey: listCampaignsRequest,
            campaigns
          });
        }
      })
    );
  }

  get campaigns(): ICampaign[] {
    return this.campaigns$.value.campaigns;
  }

  getGeoTestEditableParams(): Observable<IGeoTestEditableParams | null> {
    return this.geoTestEditableParams$.asObservable();
  }

  setGeoTestEditableParams(
    geoTestEditableParams: IGeoTestEditableParams
  ): void {
    this.geoTestEditableParams$.next(geoTestEditableParams);
  }

  clearGeoTestEditableParams(): void {
    this.geoTestEditableParams$.next(null);
  }

  isTestSettingsEdited(): boolean {
    if (this.geoTestDetails$.value && this.geoTestEditableParams$.value) {
      const {
        type,
        method,
        scaleCost: defaultScaleCost
      } = this.geoTestDetails$.value;
      const { testType, executionMethod, scaleCost } =
        this.geoTestEditableParams$.value;
      return (
        type !== testType ||
        method !== executionMethod ||
        defaultScaleCost !== scaleCost
      );
    }
    return false;
  }

  getTestName(): Observable<string> {
    return this.testName$.asObservable();
  }

  setTestName(testName: string): void {
    this.testName$.next(testName);
  }

  getBusinessQuestion(): Observable<string> {
    return this.businessQuestion$.asObservable();
  }

  setBusinessQuestion(businessQuestion: string): void {
    this.businessQuestion$.next(businessQuestion);
  }

  getEligibleGeoCell(): Observable<IGeoCell | null> {
    return this.geoCell$.asObservable();
  }

  setGeoCell(geoCell: IGeoCell, params: object) {
    this.geoCell$.next({
      ...geoCell,
      ...params
    });
  }

  createGeoTest(
    createGeoTestRequest: ICreateGeoTestRequest
  ): Observable<IGeoTest> {
    return fromPromise(
      this.apiService.geoDoeApisOperations.createGeoTest(
        this.brandStoreService.brand.clientId,
        this.brandStoreService.brand.brandId,
        createGeoTestRequest
      )
    );
  }

  updateGeoTest(
    studyId: number,
    updateGeoTestRequest: Partial<IUpdateGeoTestRequest>
  ): Observable<IGeoTest> {
    return fromPromise(
      this.apiService.geoDoeApisOperations.updateGeoTest(
        studyId,
        updateGeoTestRequest
      )
    );
  }

  fetchScaleCost(
    scaleCostRequest: IScaleCostCampaignRequest
  ): Observable<IScaleCostCampaignResponse> {
    return fromPromise(
      this.apiService.geoDoeApisOperations.getScaleCost(scaleCostRequest)
    );
  }

  setSelectedConversionTypeId(conversionTypeId: number): void {
    this.selectedConversionTypeId$.next(conversionTypeId);
  }

  setGeoTestCreatedOrUpdated(studyId?: number) {
    this.geoTestCreatedOrUpdated$.next({
      studyId: studyId || 0,
      status: studyId
        ? GeoTestPersistenceStatus.studyUpdated
        : GeoTestPersistenceStatus.studyCreated
    });
  }
}
